<template>
    <main class="main-content same-style-as-voice-designer">
        <InfoPopup v-model="showInfoPopup" :title="infoTitle" :showCancelButton="showInfoPopupCancelButton">
            <p>{{ infoMessage }}</p>
        </InfoPopup>

        <section class="voice-hero">
            <div class="hero-copy">
                <span class="eyebrow">Voice studio</span>
                <h1>Voice Gallery</h1>
                <p>
                    Browse, compare, and save production-ready voices with the same focused audio workspace used by
                    Voice Designer.
                </p>
            </div>
            <div class="stats-grid" aria-label="Voice gallery summary">
                <article class="stat-card">
                    <span>Total voices</span>
                    <strong>{{ defaultVoices.length }}</strong>
                    <p>Ready to preview and save</p>
                </article>
            </div>
        </section>

        <div class="gallery-workspace">
            <div class="gallery-main-stack">
                <section class="discovery-panel" aria-labelledby="voice-discovery-title">
                    <div class="discovery-header">
                        <div>
                            <span class="eyebrow">AI discovery</span>
                            <div id="voice-discovery-title" class="section-title" role="heading" aria-level="2">
                                Describe the voice you need
                            </div>
                            <p>Use natural language first, then refine from the recommended collection.</p>
                        </div>
                        <span class="character-count">{{ promptCharCount }} / 500</span>
                    </div>

                    <div class="ai-prompt-area">
                        <textarea v-model="prompt" rows="4"
                            placeholder="A warm documentary narrator with a calm North American accent..."></textarea>

                        <div class="prompt-examples" aria-label="Prompt examples">
                            <button v-for="example in promptExamples" :key="example.label" type="button"
                                @click="applyPromptExample(example.text)">
                                {{ example.label }}
                            </button>
                        </div>

                        <div class="prompt-actions">
                            <button class="btn btn-primary" type="button" @click="recommendVoices" :disabled="isLoading">
                                <i class="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i>
                                <span v-if="isLoading" class="spinner"></span>
                                <span v-else>Recommend Voices</span>
                            </button>
                        </div>

                        <div class="recommend-output" :class="{ active: recommendationText }" aria-live="polite">
                            <i class="fa-solid fa-sparkles" aria-hidden="true"></i>
                            <span v-if="recommendationText">{{ recommendationText }}</span>
                            <span v-else class="recommend-placeholder">
                                Recommendations will appear here and highlight fitting voices below.
                            </span>
                        </div>
                    </div>
                </section>

                <section class="gallery-shell">
                    <div class="gallery-toolbar">
                        <div>
                            <span class="eyebrow">Browse voices</span>
                            <div class="section-title" role="heading" aria-level="2">Voice collection</div>
                            <p>Search, filter, preview, and save voices in one place.</p>
                        </div>
                        <ul class="tabs" role="tablist" aria-label="Voice gallery tabs">
                            <li :class="{ active: activeTab === 'stock' }" role="tab" :aria-selected="activeTab === 'stock'"
                                tabindex="0" @click="activeTab = 'stock'" @keydown.enter="activeTab = 'stock'">
                                <i class="fa-solid fa-layer-group" aria-hidden="true"></i>
                                Stock Voices
                            </li>
                            <li :class="{ active: activeTab === 'designed' }" role="tab"
                                :aria-selected="activeTab === 'designed'" tabindex="0" @click="activeTab = 'designed'"
                                @keydown.enter="activeTab = 'designed'">
                                <i class="fa-solid fa-sliders" aria-hidden="true"></i>
                                Designed Voices
                            </li>
                        </ul>
                    </div>

                    <div class="tab-content">
                        <VGVoiceExplorerModal v-if="activeTab === 'stock'" :items="defaultVoices"
                            title="Explore stock voices" placeholder="Search by name, accent, style..." label-key="voice_name"
                            sub-label-key="model"
                            :search-keys="['voice_name', 'name', 'voice', 'accent', 'gender', 'voice_quality', 'description']"
                            v-model:searchText="searchText" v-model:recommendationIds="recommendationIds" />
                        <VGUserVoiceExplorerModal v-else-if="activeTab === 'designed'" title="Your designed voices"
                            placeholder="Search by voice name or tags..." label-key="voice_name"
                            v-model:searchText="searchText" />
                    </div>
                </section>
            </div>

            <aside class="gallery-side-panel" aria-label="Voice gallery tips">
                <section class="side-card">
                    <span class="eyebrow">Preview flow</span>
                    <div class="section-title" role="heading" aria-level="2">Listen, save, continue</div>
                    <p>Each voice card keeps playback, collect, and browse actions visible so comparison stays quick.</p>
                    <div class="mini-wave" aria-hidden="true">
                        <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i>
                    </div>
                </section>
                <section class="side-card">
                    <span class="eyebrow">Start from</span>
                    <button class="quick-start" type="button" @click="activeTab = 'stock'">
                        <span class="quick-icon">S</span>
                        <span>
                            <strong>Stock voices</strong>
                            <small>Browse built-in voices.</small>
                        </span>
                    </button>
                    <button class="quick-start" type="button" @click="activeTab = 'designed'">
                        <span class="quick-icon">D</span>
                        <span>
                            <strong>Designed voices</strong>
                            <small>Reuse your saved voices.</small>
                        </span>
                    </button>
                </section>
            </aside>
        </div>
    </main>
</template>

<script>
import { computed, ref } from 'vue';
import { requireAuth } from '@/api/auth';
import { getVoicesByUserId, getRecommendVoices } from '@/api/services'
import VGVoiceExplorerModal from '@/components/common/VGVoiceExplorerModal.vue';
import VGUserVoiceExplorerModal from '@/components/common/VGUserVoiceExplorerModal.vue';
import { checkInput } from '@/utils/InputChecker';
import InfoPopup from '@/components/common/InfoPopup.vue';

export default {
    name: 'VoiceGalleryView',
    components: { VGVoiceExplorerModal, VGUserVoiceExplorerModal, InfoPopup },
    setup() {
        const showInfoPopup = ref(false);
        const infoTitle = ref('');
        const infoMessage = ref('');
        const showInfoPopupCancelButton = ref(true);

        const prompt = ref("I'm looking for a deep, confident male voice with a British accent.");
        const recommendationText = ref('');
        const recommendationIds = ref([]);
        const isLoading = ref(false);
        const promptExamples = [
            {
                label: 'Warm podcast host',
                text: "I'm looking for a warm, natural podcast host voice with a friendly North American accent."
            },
            {
                label: 'Confident product demo',
                text: 'I need a confident, clear voice for a product demo that feels professional and trustworthy.'
            },
            {
                label: 'Calm bedtime narrator',
                text: 'Find a calm, soft narrator voice for bedtime stories with a gentle pace.'
            },
            {
                label: 'Bright social ad',
                text: 'I want an upbeat, energetic voice for a short social media advertisement.'
            }
        ];
        const promptCharCount = computed(() => prompt.value.length);

        const defaultVoices = ref([]);
        const searchText = ref('');
        async function loadVoices() {
            try {
                const voicesDefault = await getVoicesByUserId();

                defaultVoices.value = voicesDefault.filter(v => v?.model === 'Kokoro v1.0' || v?.model_name === 'Kokoro v1.0');

            } catch (err) {
                infoTitle.value = 'Error!';
                infoMessage.value = 'Failed to load voices: ' + err;
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
            }
        }
        loadVoices();

        function applyPromptExample(exampleText) {
            prompt.value = exampleText;
        }

        async function recommendVoices() {
            if (!await requireAuth('VoiceGallery.recommendVoices')) return;
            if (!prompt.value.trim()) {
                recommendationText.value = "";
                return;
            }
            const lengthLimit = 500;
            const check = checkInput(prompt.value, lengthLimit);
            if (!check.isLongEnough) {
                infoTitle.value = 'Error!';
                infoMessage.value = `Prompt is too long (${prompt.value.length} / ${lengthLimit} characters). Please shorten it.`;
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
                return;
            }
            if (!check.hasOnlyAllowed) {
                infoTitle.value = 'Error!';
                infoMessage.value = "Prompt contains invalid characters: " + check.disallowedChars.join(' ') + ". Only English letters, numbers, and common punctuation are allowed.";
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
                return;
            }
            isLoading.value = true;
            activeTab.value = 'stock';
            try {
                const response = await getRecommendVoices(prompt.value);
                const recommended = Array.isArray(response.recommended_voices) ? response.recommended_voices : [];
                const namesToAppend = [];
                const idsToRecommend = [];
                recommended.forEach(item => {
                    const vid = item.voice_id ?? item.voiceid ?? item.id;
                    const match = defaultVoices.value.find(v => v.voice_id === vid || v.id === vid);
                    if (match) {
                        const n = match.voice_name ?? match.name ?? match.voice ?? '';
                        idsToRecommend.push(vid)
                        if (n && !namesToAppend.includes(n)) namesToAppend.push(n);
                    }
                });
                recommendationText.value = response.gpt_reason;
                searchText.value = namesToAppend.length ? namesToAppend.join(' || ') : "";
                recommendationIds.value = idsToRecommend;
            } catch (err) {
                infoTitle.value = 'Error!';
                infoMessage.value = "Failed to recommend voices: " + err;
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
            } finally {
                isLoading.value = false;
            }
        }

        const activeTab = ref('stock');
        return {
            loadVoices,
            recommendVoices,
            recommendationIds,
            defaultVoices,
            searchText,
            prompt,
            recommendationText,
            isLoading,
            activeTab,
            promptExamples,
            promptCharCount,
            applyPromptExample,
            showInfoPopup,
            infoTitle,
            infoMessage,
            showInfoPopupCancelButton,
        };
    },
};
</script>

<style scoped>
.main-content {
    color: #111827;
    background:
        linear-gradient(120deg, rgba(124, 58, 237, 0.08), transparent 28%),
        linear-gradient(240deg, rgba(37, 99, 235, 0.07), transparent 30%),
        #f7f8fc;
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 100%;
    padding: 24px 28px 36px;
}

.voice-hero,
.discovery-panel,
.gallery-shell,
.side-card {
    background: rgba(255, 255, 255, 0.88);
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    box-shadow: 0 10px 34px rgba(17, 24, 39, 0.06);
}

.voice-hero {
    align-items: center;
    background:
        linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(249, 247, 255, 0.92)),
        radial-gradient(circle at 92% 12%, rgba(124, 58, 237, 0.12), transparent 30%);
    display: flex;
    gap: 18px;
    justify-content: space-between;
    padding: 22px;
    box-shadow: 0 22px 70px rgba(17, 24, 39, 0.08);
}

.hero-copy {
    max-width: 760px;
}

.eyebrow {
    color: #7c3aed;
    display: inline-flex;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    margin-bottom: 6px;
    text-transform: uppercase;
}

.voice-hero h1,
.section-title {
    color: #0f172a;
    line-height: 1.1;
    margin: 0;
    text-align: left;
}

.voice-hero h1 {
    font-size: 28px;
    line-height: 1.15;
    margin-bottom: 6px;
}

.voice-hero p {
    color: #5f6b7d;
    font-size: 1rem;
    line-height: 1.5;
    max-width: 680px;
    margin: 0;
}

.stats-grid {
    display: grid;
    grid-template-columns: minmax(160px, 220px);
    gap: 12px;
}

.stat-card {
    min-width: 160px;
    padding: 15px;
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.88);
    box-shadow: 0 10px 34px rgba(17, 24, 39, 0.06);
}

.stat-card span {
    color: #5f6b7d;
    display: block;
    font-size: 12px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
}

.stat-card strong {
    color: #111827;
    display: block;
    font-size: 25px;
    line-height: 1;
    margin-top: 8px;
}

.stat-card p,
.discovery-header p,
.gallery-toolbar p,
.side-card p,
.quick-start small {
    color: #5f6b7d;
}

.stat-card p {
    margin: 5px 0 0;
    font-size: 13px;
}

.gallery-workspace {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    gap: 20px;
    align-items: start;
}

.gallery-main-stack {
    display: grid;
    gap: 16px;
    min-width: 0;
}

.discovery-panel {
    padding: 18px;
}

.discovery-header,
.gallery-toolbar {
    align-items: center;
    display: flex;
    gap: 1rem;
    justify-content: space-between;
}

.discovery-header p,
.gallery-toolbar p {
    margin: 6px 0 0;
    line-height: 1.5;
}

.character-count {
    color: #64748b;
    font-size: 0.86rem;
    white-space: nowrap;
}

.ai-prompt-area textarea {
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    color: #111827;
    font-family: var(--app-font);
    font-size: 1rem;
    margin-bottom: 1rem;
    min-height: 100px;
    padding: 1rem 1.1rem;
    background: linear-gradient(180deg, #ffffff, #fbfcff);
    resize: vertical;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
    width: 100%;
}

.ai-prompt-area textarea:focus {
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12);
    outline: none;
}

.prompt-examples {
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
    margin-bottom: 1rem;
}

.prompt-examples button {
    background: #ffffff;
    border: 1px solid #dfe5ef;
    border-radius: 999px;
    color: #475569;
    cursor: pointer;
    font-family: var(--app-font);
    font-size: 13px;
    font-weight: 600;
    padding: 7px 10px;
    transition: border-color 180ms ease, color 180ms ease, background 180ms ease, transform 180ms ease;
}

.prompt-examples button:hover,
.prompt-examples button:focus {
    background: #f3f0ff;
    border-color: #c4b5fd;
    color: #7c3aed;
    transform: translateY(-1px);
    outline: none;
}

.prompt-actions {
    text-align: left;
}

.btn {
    align-items: center;
    border: 1px solid #dfe5ef;
    border-radius: 7px;
    cursor: pointer;
    display: inline-flex;
    font-weight: 500;
    gap: 0.5rem;
    min-height: 42px;
    padding: 10px 14px;
    text-decoration: none;
    transition: background 180ms ease, border-color 180ms ease, color 180ms ease, transform 180ms ease;
}

.btn-primary {
    background: linear-gradient(135deg, #7c3aed, #6d5dfc);
    border-color: transparent;
    color: white;
    box-shadow: 0 12px 28px rgba(109, 93, 252, 0.18);
}

.btn-primary:hover {
    background: linear-gradient(135deg, #6d28d9, #5b50e8);
    transform: translateY(-1px);
}

.btn-primary:disabled {
    cursor: not-allowed;
    opacity: 0.7;
}

.spinner {
    animation: spin 1s linear infinite;
    border: 2px solid #fff;
    border-radius: 50%;
    border-top: 2px solid var(--purple-hover);
    display: inline-block;
    height: 16px;
    width: 16px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.recommend-output {
    align-items: flex-start;
    background: #f8fafc;
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    color: var(--text-primary);
    display: flex;
    font-size: 0.95rem;
    gap: 0.7rem;
    margin-top: 1rem;
    min-height: 1.4em;
    padding: 1rem;
    white-space: pre-line;
}

.recommend-output i {
    color: #7c3aed;
    margin-top: 0.2rem;
}

.recommend-output.active {
    background: #f3f0ff;
    border-color: #c4b5fd;
}

.recommend-placeholder {
    color: var(--text-secondary);
    opacity: 0.85;
}

.gallery-shell {
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.gallery-toolbar {
    padding: 18px 18px 0;
}

.tabs {
    background: #f8fafc;
    border: 1px solid #dfe5ef;
    border-radius: 999px;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    list-style: none;
    margin: 0;
    padding: 0.25rem;
}

.tabs li {
    align-items: center;
    border-radius: 999px;
    color: #475569;
    cursor: pointer;
    display: inline-flex;
    font-size: 0.9rem;
    font-weight: 600;
    gap: 0.45rem;
    padding: 0.55rem 0.95rem;
    transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.tabs li.active {
    background: #ffffff;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
    color: #7c3aed;
}

.tab-content {
    padding-top: 0.75rem;
}

.gallery-side-panel {
    position: sticky;
    top: 24px;
    display: grid;
    gap: 16px;
}

.side-card {
    display: grid;
    gap: 12px;
    padding: 18px;
}

.section-title {
    margin: 0;
    font-size: 19px;
    font-weight: 700;
}

.side-card p {
    margin: 0;
    line-height: 1.5;
}

.mini-wave {
    height: 34px;
    display: flex;
    align-items: center;
    gap: 3px;
}

.mini-wave i {
    display: block;
    width: 4px;
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(124, 58, 237, 0.82), rgba(37, 99, 235, 0.4));
}

.mini-wave i:nth-child(1) { height: 9px; }
.mini-wave i:nth-child(2) { height: 18px; }
.mini-wave i:nth-child(3) { height: 13px; }
.mini-wave i:nth-child(4) { height: 25px; }
.mini-wave i:nth-child(5) { height: 17px; }
.mini-wave i:nth-child(6) { height: 29px; }
.mini-wave i:nth-child(7) { height: 12px; }
.mini-wave i:nth-child(8) { height: 22px; }
.mini-wave i:nth-child(9) { height: 15px; }
.mini-wave i:nth-child(10) { height: 26px; }

.quick-start {
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr);
    gap: 10px;
    padding: 12px;
    border: 1px solid #dfe5ef;
    border-radius: 7px;
    background: rgba(255, 255, 255, 0.86);
    text-align: left;
    cursor: pointer;
    transition: background 180ms ease, border-color 180ms ease, transform 180ms ease;
}

.quick-start:hover {
    border-color: #c4b5fd;
    background: #fbfaff;
    transform: translateY(-1px);
}

.quick-icon {
    width: 34px;
    height: 34px;
    display: grid;
    place-items: center;
    border-radius: 7px;
    background: #f3f0ff;
    color: #7c3aed;
    font-weight: 700;
}

.quick-start strong,
.quick-start small {
    display: block;
}

.quick-start small {
    margin-top: 2px;
    line-height: 1.35;
}

.gallery-shell :deep(.pm-modal) {
    padding: 18px;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    background: transparent;
}

.gallery-shell :deep(.pm-header),
.gallery-shell :deep(.pm-header > div),
.gallery-shell :deep(.pm-eyebrow),
.gallery-shell :deep(.pm-header h3),
.gallery-shell :deep(.vc-name-tags),
.gallery-shell :deep(.vc-name-row),
.gallery-shell :deep(.vc-tags),
.gallery-shell :deep(.vc-description) {
    text-align: left;
}

.gallery-shell :deep(.pm-search-wrap),
.gallery-shell :deep(.pm-controls-row),
.gallery-shell :deep(.vc-card) {
    border-color: #dfe5ef;
    border-radius: 8px;
}

.gallery-shell :deep(.vc-card) {
    transition: background 180ms ease, border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
}

.gallery-shell :deep(.vc-card:hover) {
    border-color: #c4b5fd;
    background: #ffffff;
    box-shadow: 0 16px 38px rgba(17, 24, 39, 0.09);
    transform: translateY(-2px);
}

button:focus-visible,
.tabs li:focus-visible {
    outline: 3px solid rgba(124, 58, 237, 0.24);
    outline-offset: 2px;
}

@media (max-width: 900px) {

    .voice-hero,
    .discovery-header,
    .gallery-toolbar {
        align-items: stretch;
        flex-direction: column;
    }

    .stats-grid {
        width: 100%;
        grid-template-columns: 1fr;
    }

    .gallery-workspace {
        grid-template-columns: 1fr;
    }

    .gallery-side-panel {
        position: static;
    }
}

@media (max-width: 640px) {
    .main-content {
        padding: 16px;
    }

    .voice-hero {
        padding: 18px;
    }

    .voice-hero h1 {
        font-size: 28px;
    }

    .tabs {
        border-radius: 14px;
    }

    .tabs li {
        flex: 1;
        justify-content: center;
    }

    .prompt-actions {
        text-align: left;
    }
}
</style>
